import { useContext, useMemo } from "react";
import { AxiosError } from "axios";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { authReducerActionType } from "../../types/globalTypes";
import {
  getAllProjects,
  getApplicationSuggestions,
  getBillingCheckmarks,
  getCanPL,
  getCanQM,
  getCoreCompetencies,
  getTenderedProjects,
  saveApplication as saveApplicationApi,
} from "../../api/projects";
import { ApplicationSuggestionsDto, ProjectApplicationDto } from "../../types/projectTypes";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";

/**
 * Hook that handles the project api calls, uses react-query
 */
const useProjects = () => {
  const { auth, dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // ############
  // GET QUERIES
  // ############

  // getAllProjects query
  const { data: currentProjects } = useQuery({
    queryKey: ["projects"],
    queryFn: getAllProjects,
  });
  const allProjects = useMemo(() => (currentProjects ? currentProjects.data : []), [currentProjects]);

  // getTenderedProjectsDto query
  const { data: currentTenderedProjects } = useQuery({
    queryKey: ["tenderedProjects"],
    queryFn: getTenderedProjects,
  });
  const tenderedProjects = useMemo(
    () => (currentTenderedProjects ? currentTenderedProjects.data : []),
    [currentTenderedProjects]
  );

  // getBillingCheckmarksDto query
  const { data: currentBillingCheckmarks } = useQuery({
    queryKey: ["billingCheckmarks"],
    queryFn: () => getBillingCheckmarks(),
    // only fetch the billing checkmarks if the user has the necessary permissions
    // otherwise the query would be executed everytime for the project overview, which is will result in a lot of unnecessary requests
    enabled: !!doesPermissionsHaveSomeOf(auth?.permissions, [7]),
  });
  const projectsWithBillingCheckmarks = currentBillingCheckmarks ? currentBillingCheckmarks.data : [];

  // getAllCoreCompetencies query
  const { data: coreCompetenciesData } = useQuery({
    queryKey: ["coreCompetencies"],
    queryFn: () => getCoreCompetencies(),
  });
  const allCoreCompetencies = coreCompetenciesData ? coreCompetenciesData.data : [];

  /**
   * Checks if the member has a PL qualification
   * @param memberId The ID of the member to check
   * @returns A promise that resolves to true if the member has a PL qualification, false otherwise
   */
  const checkHasPlQualification = async (memberId: number) => {
    const data = await queryClient.fetchQuery({
      queryKey: ["canPL", memberId],
      queryFn: () => getCanPL(memberId),
    });
    return data?.data;
  };

  /**
   * Checks if the member has a QM qualification
   * @param memberId The ID of the member to check
   * @returns A promise that resolves to true if the member has a QM qualification, false otherwise
   */
  const checkHasQMQualification = async (memberId: number) => {
    const data = await queryClient.fetchQuery({
      queryKey: ["canQM", memberId],
      queryFn: () => getCanQM(memberId),
    });
    return data?.data;
  };

  // getApplicationSuggestions query
  const { data: applicationSuggestionsData } = useQuery({
    queryKey: ["applicationSuggestions"],
    queryFn: () => getApplicationSuggestions(auth?.userID ?? 0),
  });

  const applicationSuggestions: ApplicationSuggestionsDto = applicationSuggestionsData
    ? applicationSuggestionsData.data
    : {
        ItSkills: [],
        internships: [],
        visitedWorkshops: [],
        positionsOfMember: [],
        workshopsHeldByMember: [],
      };

  // saveApplication mutation
  const { mutateAsync: saveApplicationMutation } = useMutation({
    mutationFn: saveApplicationApi,
    onError: (err: AxiosError) => {
      showErrorMessage("Bewerbung speichern fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: () => {
      showSuccessMessage("Bewerbung erfolgreich gespeichert");
    },
  });

  /**
   * Saves the application for the project
   * @param application The application to save
   * @returns A promise that resolves when the application is saved
   */
  const saveApplication = async (application: ProjectApplicationDto) => {
    return await saveApplicationMutation(application);
  };

  return {
    allProjects,
    tenderedProjects,
    projectsWithBillingCheckmarks,
    allCoreCompetencies,
    checkHasPlQualification,
    checkHasQMQualification,
    applicationSuggestions,
    saveApplication,
  };
};

export default useProjects;
